import Head from 'next/head'
import { useIntl } from 'react-intl'

/**
 * Pass a string or array of strings/keys. Keys will be localized. If you need
 * a localized string with values, do it yourself and pass the resulting string.
 *
 * Note: we just have <PageTitle> vs something like <PageMeta> because we have
 * no SEO traffic or social media sharing, etc. Thus no description, canonical
 * URL, image, video, etc. The business is not and will never be public-facing.
 *
 * If no title is provided, defaults to "B-Stock". If a title is provided,
 * it uses that title exactly without prepending "B-Stock".
 *
 * Browser title display varies by context:
 * - Back button menu (click and hold) shows 92 + ellipsis
 * - Hovering over a tab title shows 58 + ellipsis
 * - Unhovered, unshrunk tab title shows 25, fading out
 *
 * @usage
 *   1. in _app.page.ts, add default `<PageTitle />` (renders "B-Stock")
 *
 *   2. override the default page title in each page.
 *
 *   `<PageTitle value="foo" />` renders "foo"
 *
 *   `<PageTitle value="iPhone 13 | Apple Store" />`
 *   renders "iPhone 13 | Apple Store"
 *
 * @todo Migrate to bstock-next
 */
export const PageTitle = ({
  value: input,
}: {
  value?: string | null | Array<string | undefined | null>
}) => {
  const { formatMessage, messages } = useIntl()

  const maybeKeys = (
    input ? (Array.isArray(input) ? input : [input]) : []
  ).filter(Boolean) as string[]

  const parts = maybeKeys.map((x) =>
    messages[x] ? formatMessage({ id: x }) : x
  )

  // Use "B-Stock" as fallback if no parts exist
  const title = parts.length > 0 ? parts.join(' | ') : 'B-Stock'

  return (
    <Head>
      <title key="page-title">{title}</title>
    </Head>
  )
}
